import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { ClipboardList, Plus, Trash2, ChevronDown, ChevronUp, Info } from "lucide-react";
import { API } from "../../App";
import { useClinicalResource, fmtDate } from "./shared";
import { StInventoryForm } from "./StInventoryForm";
import { StScoreBars } from "./StScoreBars";

const LEVEL_BADGE = { eleve: "bg-rose-100 text-rose-700", modere: "bg-amber-100 text-amber-700", faible: "bg-emerald-100 text-emerald-700" };
const LEVEL_LABEL = { eleve: "élevé", modere: "modéré", faible: "faible" };

const EntryCard = ({ e, inst, domains, onDelete }) => {
  const [open, setOpen] = useState(false);
  const labels = Object.fromEntries(inst.dims.map((d) => [d.key, d.label]));
  const top = Object.entries(e.scoring.dims).filter(([, v]) => v.level !== "faible").sort((a, b) => b[1].mean - a[1].mean).slice(0, 4);
  return (
    <div className="rounded-xl border bg-card p-4 space-y-2" data-testid={`stq-entry-${e.entry_id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <p className="font-medium text-sm">{inst.label}{e.label ? ` — ${e.label}` : ""}</p>
          <p className="text-xs text-muted-foreground">Passation du {fmtDate(e.date)} · {e.mode === "scores" ? "scores saisis (instrument officiel)" : `${e.scoring.answered}/${e.scoring.total} items`}{e.note ? ` · ${e.note}` : ""}</p>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => setOpen(!open)} data-testid={`stq-toggle-${e.entry_id}`}>{open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} {open ? "Réduire" : "Détail"}</Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={onDelete} data-testid={`stq-delete-${e.entry_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid={`stq-top-${e.entry_id}`}>
        {top.length === 0 && <span className="text-xs text-muted-foreground">Aucune dimension modérée ou élevée</span>}
        {top.map(([k, v]) => <span key={k} className={`text-xs px-2 py-0.5 rounded-full ${LEVEL_BADGE[v.level]}`}>{labels[k]} · {v.mean.toFixed(1)} ({LEVEL_LABEL[v.level]})</span>)}
      </div>
      {open && <StScoreBars scoring={e.scoring} dims={inst.dims} domains={e.instrument === "ysq" ? domains : null} />}
    </div>
  );
};

export const StQuestionnairesPanel = ({ patientId }) => {
  const [res, setRes, error, reload] = useClinicalResource(patientId, "st-questionnaires");
  const [creating, setCreating] = useState(null); // instrument en cours de passation
  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!res) return <p className="text-sm text-muted-foreground">Chargement…</p>;
  const remove = async (e) => {
    if (!window.confirm("Supprimer cette passation ?")) return;
    try { await axios.delete(`${API}/patients/${patientId}/clinical/st-questionnaires/${e.entry_id}`, { withCredentials: true }); setRes({ ...res, entries: res.entries.filter((x) => x.entry_id !== e.entry_id) }); toast.success("Passation supprimée"); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  if (creating) return <StInventoryForm patientId={patientId} instrument={creating} spec={res.instruments[creating]} onCancel={() => setCreating(null)} onSaved={() => { setCreating(null); reload(); }} />;
  return (
    <div className="space-y-4" data-testid="stq-panel">
      <div className="rounded-xl border border-sky-200 bg-sky-50/60 dark:bg-sky-950/20 p-3 text-sm flex items-start gap-2"><Info className="w-4 h-4 mt-0.5 text-sky-600 shrink-0" /><span>Inventaires de suivi aux formulations originales, inspirés du YSQ-S3 (18 schémas) et du SMI (14 modes). Repères non normés : ils orientent l'exploration clinique et la conceptualisation, sans valeur diagnostique. Si vous utilisez les instruments officiels, saisissez directement les scores moyens.</span></div>
      <div className="grid sm:grid-cols-2 gap-3">
        {Object.entries(res.instruments).map(([k, inst]) => (
          <div key={k} className="rounded-xl border bg-card p-4 flex flex-col gap-2" data-testid={`stq-instrument-${k}`}>
            <p className="font-medium text-sm flex items-center gap-2"><ClipboardList className="w-4 h-4 text-primary" /> {inst.label}</p>
            <p className="text-xs text-muted-foreground">{inst.items.length} items · échelle 1-6 · {inst.dims.length} dimensions · {res.entries.filter((e) => e.instrument === k).length} passation(s)</p>
            <Button size="sm" className="gap-1.5 self-start mt-auto" onClick={() => setCreating(k)} data-testid={`stq-new-${k}`}><Plus className="w-3.5 h-3.5" /> Nouvelle passation</Button>
          </div>
        ))}
      </div>
      {res.entries.length === 0 ? (
        <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="stq-empty">Aucune passation enregistrée. Lancez un inventaire avec le patient en séance ou saisissez les scores d'un instrument officiel.</div>
      ) : res.entries.map((e) => <EntryCard key={e.entry_id} e={e} inst={res.instruments[e.instrument]} domains={res.domains} onDelete={() => remove(e)} />)}
    </div>
  );
};
